import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import BreadcrumbGroup, { BreadcrumbGroupProps } from '@cloudscape-design/components/breadcrumb-group';
import { useLocation } from 'react-router-dom';


export interface Breadcrumb {
  text: string;
  href: string;
  index: number;
}

interface BreadcrumbContextType {
  breadcrumbs: Breadcrumb[];
  setActiveBreadcrumb: (newBreadcrumb: Breadcrumb, maxIndex?: number) => void;
  resetBreadcrumbs: () => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

interface BreadcrumbProviderProps {
  children: ReactNode;
}

export function BreadcrumbProvider({ children }: BreadcrumbProviderProps): JSX.Element {
  const isFirstMount = React.useRef(true);

  useEffect(() => {
    console.log('MyContextProvider mounted');
    
    if (isFirstMount.current) {
      localStorage.removeItem('breadcrumbs');
      isFirstMount.current = false;
    }
    
    return () => {
      console.log('MyContextProvider unmounted');
    };
  }, []);

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>(() => {
    try {
      const savedBreadcrumbs = localStorage.getItem('breadcrumbs');
      return savedBreadcrumbs 
        ? JSON.parse(savedBreadcrumbs) 
        : [{ text: 'Home', href: '/', index: 0 }];
    } catch (error) {
      console.error('Error loading breadcrumbs from localStorage:', error);
      return [{ text: 'Home', href: '/', index: 0 }];
    }
  });
  
  useEffect(() => {
    try {
      localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));
    } catch (error) {
      console.error('Error saving breadcrumbs to localStorage:', error);
    }
  }, [breadcrumbs]);
  
  const setActiveBreadcrumb = (newBreadcrumb: Breadcrumb, maxIndex: number = Infinity): void => {
    if (!newBreadcrumb || !newBreadcrumb.text || !newBreadcrumb.href || typeof newBreadcrumb.index !== 'number') {
      console.error('Invalid breadcrumb format. Each breadcrumb needs text, href, and index properties.');
      return;
    }
    
    const filteredBreadcrumbs = breadcrumbs
      .filter(crumb => crumb.index !== newBreadcrumb.index)
      .filter(crumb => crumb.index < maxIndex);
    
    const newBreadcrumbs = [...filteredBreadcrumbs, newBreadcrumb]
      .sort((a, b) => a.index - b.index);
    setBreadcrumbs(newBreadcrumbs);
  };
  
  const resetBreadcrumbs = (): void => {
    setBreadcrumbs([{ text: 'Home', href: '/', index: 0 }]);
  };

  return (
    <BreadcrumbContext.Provider value={{ 
      breadcrumbs, 
      setActiveBreadcrumb,
      resetBreadcrumbs
    }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbs(): BreadcrumbContextType {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return context;
}


export default function Breadcrumbs(): JSX.Element | null {
  const { breadcrumbs } = useBreadcrumbs();
  const location = useLocation();
  
  if (location.pathname === '/authentiate') {
    return null;
  }
  
  const breadcrumbItems: BreadcrumbGroupProps.Item[] = breadcrumbs.map(({ text, href }) => ({
    text,
    href
  }));
  
  return <BreadcrumbGroup items={breadcrumbItems} />;
}
